/*
 * intro.js — INTRO cinemática AUTOCONTENIDA, replicada en Three.js.
 * ============================================================================
 * Recuperada del sitio (outputs/sitio: #intro + hero-particles.js `mountCoordField`).
 * Es un INTRO propio, NO un overlay del túnel: tiene su PROPIO canvas (#intro-canvas) con un
 * TORRENTE DE GLIFOS DE CÓDIGO ({ } [ ] < > / => </> …) que fluye hacia la cámara con glow aditivo,
 * y encima el wordmark "@YaNiPaper" + eyebrow + subtítulo + barra de carga + SKIP. Corre PRIMERO;
 * al terminar (o SKIP/Escape) se funde y revela el túnel. Paleta y fuentes del canon
 * (docs/canones-esteticos.md): tintes de glifo cian/magenta/verde; wordmark Bungee magenta+verde.
 *
 * El motor de glifos es un port fiel a Three.js del `mountCoordField` OGL del sitio: atlas de símbolos
 * en canvas 2D → textura; nube de Points con índice de glifo + tinte + tamaño por partícula; el vertex
 * shader integra el avance (uTravel) con wrap hacia la cámara; fragment muestrea la celda del atlas y
 * la tiñe; blending aditivo. Sin OGL.
 */

import * as THREE from 'three';
import { createMusic } from './music.js';
import { mountRobot } from './robot.js';

const WORD = '@YaNiPaper';
/* Línea de tiempo del intro (petición de Héctor): dura 22s en total; los últimos 2s la música hace
   fadeout para no cortarse de golpe. */
const INTRO_MS = 22000;   // duración total del intro
const FADE_MS = 2000;     // fadeout de la música en los últimos 2s
/* URL del mp3 servido desde public/audio/ (BASE_URL respeta un despliegue bajo subruta). */
const MUSIC_URL = `${import.meta.env.BASE_URL}audio/cumbia-de-la-noche.mp3`;
/* Video del robot (fondo verde, chroma key en vivo). Servido del mismo origen (public/video/). */
const ROBOT_URL = `${import.meta.env.BASE_URL}video/robot.mp4`;
const PAUSES = [0.34, 0.58, 0.82];

/* Símbolos de programación (NO ceros/unos), como el torrente original. */
const CODE_GLYPHS = [
  '{', '}', '[', ']', '(', ')', '<', '>', '/', '\\', ';', ':', '=', '+', '-',
  '*', '&', '|', '!', '?', '#', '@', '$', '%', '^', '~', '=>', '</>',
];
/* Tintes = las 5 tintas neón del canon (vec3 normalizados): cian · magenta · verde · amarillo · bermellón. */
const GLYPH_TINTS = [
  [0x00 / 255, 0xe5 / 255, 0xff / 255], // cian      #00E5FF
  [0xff / 255, 0x0f / 255, 0x9e / 255], // magenta   #FF0F9E
  [0x39 / 255, 0xff / 255, 0x14 / 255], // verde     #39FF14
  [0xff / 255, 0xe6 / 255, 0x00 / 255], // amarillo  #FFE600
  [0xff / 255, 0x3b / 255, 0x1f / 255], // bermellón #FF3B1F
];

/* Dibuja el atlas de glifos (blanco; el tinte lo pone el shader por partícula). */
function buildGlyphAtlas() {
  const count = CODE_GLYPHS.length;
  const cols = 8;
  const rows = Math.ceil(count / cols);
  const cell = 64;
  const cvs = document.createElement('canvas');
  cvs.width = cols * cell;
  cvs.height = rows * cell;
  const ctx = cvs.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  CODE_GLYPHS.forEach((g, i) => {
    const cx = (i % cols) * cell + cell / 2;
    const cy = Math.floor(i / cols) * cell + cell / 2;
    ctx.font = Math.floor(cell * (g.length > 1 ? 0.34 : 0.62)) + "px ui-monospace, 'Courier New', monospace";
    ctx.fillText(g, cx, cy);
  });
  return { canvas: cvs, cols, rows, count };
}

/* Monta el torrente de glifos en Three.js sobre `canvas`. Devuelve API o null si no hay WebGL. */
function mountGlyphTorrent(canvas) {
  // Pre-chequeo: si el navegador no da contexto WebGL, NO instanciamos el renderer — THREE.WebGLRenderer
  // loguea 3 console.error antes de lanzar. Sondeamos con un canvas desechable y degradamos limpio.
  const probe = document.createElement('canvas');
  const gl = probe.getContext('webgl2') || probe.getContext('webgl') || probe.getContext('experimental-webgl');
  if (!gl) return null;
  gl.getExtension('WEBGL_lose_context')?.loseContext(); // liberar el contexto de sondeo
  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, powerPreference: 'low-power' });
  } catch (e) { return null; }
  const dpr = Math.min(window.devicePixelRatio || 1, 1.75);
  renderer.setPixelRatio(dpr);
  renderer.setSize(window.innerWidth, window.innerHeight);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(70, window.innerWidth / window.innerHeight, 0.1, 140);
  camera.position.set(0, 0, 6);

  const atlas = buildGlyphAtlas();
  const tex = new THREE.CanvasTexture(atlas.canvas);
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;

  const COUNT = 2200;
  const SPREAD_X = 15.0, SPREAD_Y = 11.0, DEPTH = 60.0, WRAP_FRONT = 5.4;
  const RANGE = DEPTH + WRAP_FRONT;

  const positions = new Float32Array(COUNT * 3);
  const aGlyph = new Float32Array(COUNT);
  const aTint = new Float32Array(COUNT * 3);
  const aSize = new Float32Array(COUNT);
  for (let k = 0; k < COUNT; k++) {
    positions[k * 3 + 0] = (Math.random() - 0.5) * SPREAD_X * 2.0;
    positions[k * 3 + 1] = (Math.random() - 0.5) * SPREAD_Y * 2.0;
    positions[k * 3 + 2] = -Math.random() * DEPTH;
    aGlyph[k] = Math.floor(Math.random() * atlas.count);
    const t = GLYPH_TINTS[(Math.random() * GLYPH_TINTS.length) | 0];
    aTint[k * 3 + 0] = t[0]; aTint[k * 3 + 1] = t[1]; aTint[k * 3 + 2] = t[2];
    aSize[k] = (Math.random() < 0.5 ? 0.95 : 0.6);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geo.setAttribute('aGlyph', new THREE.BufferAttribute(aGlyph, 1));
  geo.setAttribute('aTint', new THREE.BufferAttribute(aTint, 3));
  geo.setAttribute('aSize', new THREE.BufferAttribute(aSize, 1));

  const uniforms = {
    uTime: { value: 0 },
    uDpr: { value: dpr },
    uTravel: { value: 0 },
    uDepth: { value: DEPTH },
    uRange: { value: RANGE },
    uScaleK: { value: 260.0 },
    uOpacity: { value: 0.98 },
    uAtlas: { value: tex },
    uCols: { value: atlas.cols },
    uRows: { value: atlas.rows },
  };

  // ShaderMaterial: three inyecta position/modelViewMatrix/projectionMatrix; no redeclarar.
  const mat = new THREE.ShaderMaterial({
    uniforms,
    transparent: true,
    depthTest: false,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    vertexShader: /* glsl */`
      attribute float aGlyph;
      attribute vec3 aTint;
      attribute float aSize;
      uniform float uTime, uDpr, uTravel, uDepth, uRange, uScaleK, uOpacity;
      varying vec3 vTint;
      varying float vGlyph;
      varying float vAlpha;
      void main() {
        vec3 p = position;
        p.x += sin(uTime * 0.10 + aGlyph) * 0.10;      // deriva lateral orgánica
        p.y += cos(uTime * 0.08 + aGlyph * 1.3) * 0.08;
        float z = mod(p.z + uTravel, uRange) - uDepth; // flujo hacia la cámara con wrap
        float nz = (z + uDepth) / uRange;              // 0 lejos … 1 roza cámara
        float edge = smoothstep(0.0, 0.06, nz) * smoothstep(1.0, 0.86, nz);
        vTint = aTint; vGlyph = aGlyph; vAlpha = uOpacity * edge;
        vec4 mv = modelViewMatrix * vec4(p.x, p.y, z, 1.0);
        gl_PointSize = aSize * uScaleK * uDpr * (1.0 / max(-mv.z, 0.35));
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */`
      precision mediump float;
      uniform sampler2D uAtlas;
      uniform float uCols, uRows;
      varying vec3 vTint;
      varying float vGlyph;
      varying float vAlpha;
      void main() {
        float idx = floor(vGlyph + 0.5);
        float col = mod(idx, uCols);
        float row = floor(idx / uCols);
        vec2 cell = vec2(1.0 / uCols, 1.0 / uRows);
        vec2 uv;
        uv.x = (col + gl_PointCoord.x) * cell.x;
        uv.y = 1.0 - (row + gl_PointCoord.y) * cell.y;
        vec4 tx = texture2D(uAtlas, uv);
        float a = tx.a * vAlpha;
        if (a < 0.02) discard;
        gl_FragColor = vec4(vTint, a);
      }
    `,
  });

  const points = new THREE.Points(geo, mat);
  scene.add(points);

  function resize() {
    renderer.setSize(window.innerWidth, window.innerHeight);
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
  }
  window.addEventListener('resize', resize);

  let raf = null, running = true;
  let last = performance.now();
  let travel = 0, speed = 1;
  const BASE = 6.0; // unidades/segundo a speed=1
  function frame() {
    if (!running) { raf = null; return; }
    raf = requestAnimationFrame(frame);
    const now = performance.now();
    const dt = Math.min((now - last) / 1000, 0.05);
    last = now;
    travel += dt * speed * BASE;
    uniforms.uTravel.value = travel;
    uniforms.uTime.value = (now / 1000);
    renderer.render(scene, camera);
  }
  frame();

  return {
    setSpeed(s) { speed = s; },
    dispose() {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      geo.dispose(); mat.dispose(); tex.dispose(); renderer.dispose();
    },
  };
}

/* Parte el wordmark en <span class="ch"> por letra. */
function buildWord(el) {
  const text = el.dataset.text || WORD;
  el.innerHTML = '';
  const spans = [];
  for (const ch of [...text]) {
    const s = document.createElement('span');
    s.className = 'ch';
    s.textContent = ch;
    el.appendChild(s);
    spans.push(s);
  }
  return spans;
}

/*
 * Corre la intro autocontenida.
 * @param {Object} opts
 * @param {Function} opts.onDone        — al cerrar (revelar el túnel).
 * @param {boolean}  opts.reducedMotion — si true, salta la intro (fallback digno / HALL-A).
 */
/*
 * Ecualizador reactivo: llena `container` con barras y las anima con `levelsFn(n)` (espectro en vivo
 * del mp3, [0..1] por barra). Si no hay niveles (sin audio), cae a una animación idle suave por seno.
 * Ataque rápido / caída lenta para un movimiento con "cuerpo". Devuelve una función para detenerlo.
 */
function mountEqualizer(container, levelsFn) {
  if (!container) return () => {};
  const N = 28;
  // tintas del canon, cicladas por barra
  const PAL = ['#00e5ff', '#ff0f9e', '#39ff14', '#ffe600', '#ff3b1f'];
  const bars = [];
  container.textContent = '';
  for (let i = 0; i < N; i++) {
    const b = document.createElement('span');
    b.className = 'intro-eq-bar';
    b.style.color = PAL[i % PAL.length];
    container.appendChild(b);
    bars.push(b);
  }
  container.classList.add('visible');

  const smooth = new Float32Array(N);
  let raf = null, running = true;
  function frame() {
    if (!running) return;
    raf = requestAnimationFrame(frame);
    const lv = levelsFn ? levelsFn(N) : null;
    const now = performance.now();
    for (let i = 0; i < N; i++) {
      // objetivo: espectro real, o idle por seno si no hay audio
      const target = lv ? lv[i] : (0.28 + 0.22 * Math.sin(now / 240 + i * 0.5));
      const up = target > smooth[i];
      smooth[i] += (target - smooth[i]) * (up ? 0.6 : 0.12); // ataque rápido, caída lenta
      const s = 0.08 + Math.max(0, Math.min(1, smooth[i])) * 0.92;
      bars[i].style.transform = `scaleY(${s.toFixed(3)})`;
    }
  }
  frame();
  return () => { running = false; if (raf) cancelAnimationFrame(raf); };
}

function runSequence(hooks = {}) {
  const intro = document.getElementById('intro');
  if (!intro) { if (hooks.onClose) hooks.onClose(); return; }

  document.body.classList.add('intro-active');

  const canvas = intro.querySelector('#intro-canvas');
  const wordEl = intro.querySelector('.intro-word');
  const loadWrap = intro.querySelector('.intro-load');
  const loadBar = intro.querySelector('.intro-load-bar');
  const loadPct = intro.querySelector('.intro-load-pct');
  const skip = intro.querySelector('.intro-skip');

  // Motor de glifos (la "animación detrás"). Si WebGL falla, la intro sigue con solo el texto.
  const field = canvas ? mountGlyphTorrent(canvas) : null;

  if (wordEl) buildWord(wordEl); // se parte en letras solo para el fade; SIN scramble (wordmark estático)
  requestAnimationFrame(() => intro.classList.add('is-in'));

  // Ecualizador reactivo al mp3 (bajo la barra de carga). levelsFn viene de la música (analyser).
  const stopEq = mountEqualizer(intro.querySelector('.intro-eq'), hooks.levels);

  let loadRaf = null, stopped = false, done = false, fadeStarted = false;
  let fadeTimer = null, closeTimer = null;

  // Barra de carga cosmética: recorre casi todo el intro (no dispara el cierre; eso lo hace el reloj maestro).
  function runLoad() {
    if (!loadBar || !loadPct) return;
    if (loadWrap) loadWrap.classList.add('visible');
    const dur = INTRO_MS - 600; // llega a ~100% poco antes del fade
    const t0 = performance.now();
    function frame(now) {
      if (stopped) return;
      const lin = Math.min((now - t0) / dur, 1);
      let p = 1 - Math.pow(1 - lin, 2);
      for (const pt of PAUSES) { if (p > pt - 0.04 && p < pt + 0.02) p = pt - 0.02; }
      const pct = Math.max(0, Math.min(100, Math.round(p * 100)));
      loadBar.style.width = pct + '%';
      loadPct.textContent = pct + '%';
      if (lin < 1) loadRaf = requestAnimationFrame(frame);
      else { loadBar.style.width = '100%'; loadPct.textContent = '100%'; }
    }
    loadRaf = requestAnimationFrame(frame);
  }

  // A los 20s (INTRO_MS − FADE_MS): arranca el fadeout de 2s de la música + rush del torrente.
  function startFade() {
    if (fadeStarted || done) return;
    fadeStarted = true;
    if (field) field.setSpeed(8);
    if (hooks.onFadeStart) hooks.onFadeStart(); // música: fadeout de FADE_MS
  }

  function finish() {
    if (done) return;
    done = true;
    stopped = true;
    if (loadRaf) cancelAnimationFrame(loadRaf);
    if (fadeTimer) clearTimeout(fadeTimer);
    if (closeTimer) clearTimeout(closeTimer);
    if (loadBar) loadBar.style.width = '100%';
    if (loadPct) loadPct.textContent = '100%';
    // SKIP/Escape antes del fade natural → corte suave rápido de la música.
    if (!fadeStarted) {
      if (field) field.setSpeed(8);
      if (hooks.onFinishStart) hooks.onFinishStart();
    }
    intro.classList.add('is-out');
    if (stopEq) stopEq();
    setTimeout(() => {
      if (field) field.dispose();
      if (hooks.robot) hooks.robot.dispose();
      intro.style.display = 'none';
      intro.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('intro-active');
      if (hooks.onClose) hooks.onClose();
    }, 850);
  }

  if (skip) skip.addEventListener('click', finish);
  const onKey = (e) => {
    if (e.key === 'Escape' || e.key === 'Enter') {
      finish();
      document.removeEventListener('keydown', onKey);
    }
  };
  document.addEventListener('keydown', onKey);

  // Reloj maestro de 22s: fade de música a los 20s, cierre del intro a los 22s.
  runLoad();
  fadeTimer = setTimeout(startFade, INTRO_MS - FADE_MS);
  closeTimer = setTimeout(finish, INTRO_MS);
}

/*
 * Punto de entrada: PANTALLA "ENTRAR" → audio + intro → túnel.
 * El clic en "Entrar" es el gesto de usuario que desbloquea el AudioContext (los navegadores no dejan
 * sonar audio sin interacción). Arranca la cumbia sintetizada y el intro juntos; al cerrar el intro,
 * la cumbia se funde y se revela el túnel. reduced-motion / sin cinema → sin gate, sin audio, sin
 * intro: contenido directo (HALL-A).
 */
export function startIntro(opts = {}) {
  const onDone = opts.onDone || (() => {});
  const gate = document.getElementById('gate');
  const intro = document.getElementById('intro');

  if (opts.reducedMotion || !intro) {
    if (gate) gate.remove();
    if (intro) intro.style.display = 'none';
    onDone();
    return;
  }

  // Volver desde un post: sin gate ni intro cinemática → home directo (hero + notas). El túnel y el
  // scroll los monta main.js aparte; el hero (.chapter--intro) siempre está visible. Sin música/robot,
  // que dependen del gesto de "Entrar".
  if (opts.skipEntry) {
    if (gate) gate.remove();
    intro.style.display = 'none';
    intro.setAttribute('aria-hidden', 'true');
    onDone();
    return;
  }

  if (!gate) { runSequence({ onClose: onDone }); return; } // sin gate: intro sin audio

  const enterBtn = gate.querySelector('.gate-enter');
  const enter = () => {
    if (enterBtn) enterBtn.removeEventListener('click', enter);

    // Audio: el clic es el gesto que permite sonar. Si algo falla, el intro sigue en silencio.
    let music = null;
    try {
      music = createMusic(MUSIC_URL);
      music.start();
    } catch (e) { music = null; }

    // Robot con chroma key: mismo gesto desbloquea la reproducción del video. Si no hay WebGL, null.
    let robot = null;
    try {
      const robotCanvas = document.getElementById('intro-robot-canvas');
      if (robotCanvas) { robot = mountRobot(robotCanvas, ROBOT_URL); if (robot) robot.start(); }
    } catch (e) { robot = null; }

    gate.classList.add('is-out');
    setTimeout(() => { if (gate.parentNode) gate.remove(); }, 700);

    runSequence({
      robot,
      // espectro en vivo del mp3 para el ecualizador (null si no hay audio → idle por seno)
      levels: (n) => (music ? music.getLevels(n) : null),
      // a los 20s: fadeout de 2s (no se corta de golpe)
      onFadeStart: () => { if (music) music.stop(FADE_MS / 1000); },
      // SKIP/Escape antes de tiempo: corte suave rápido
      onFinishStart: () => { if (music) music.stop(0.6); },
      onClose: onDone,
    });
  };
  if (enterBtn) {
    enterBtn.addEventListener('click', enter);
    enterBtn.focus();
  }
}
